import { useEffect, useState } from "react";
import { AutoComplete, Input } from "antd";
import { useNavigate } from "react-router-dom";

import { type UserRow, api } from "../api/client";

interface Props {
  currentUserId: number;
}

interface Option {
  value: string;
  label: string;
  user: UserRow;
}

/**
 * Top-of-page autocomplete for jumping directly between users on the
 * detail view. Debounced 200ms so each keystroke doesn't fire its own
 * request.
 */
export const UserQuickSwitch = ({ currentUserId }: Props) => {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (!value || value.length < 1) {
      setOptions([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        const r = await api.listUsers({ q: value, page_size: 20 });
        setOptions(
          r.items
            .filter((u) => u.strichliste_user_id !== currentUserId)
            .map((u) => ({
              value: String(u.strichliste_user_id),
              label: `${u.name}${u.email ? ` — ${u.email}` : ""}`,
              user: u,
            })),
        );
      } catch {
        setOptions([]);
      }
    }, 200);
    return () => window.clearTimeout(handle);
  }, [value, currentUserId]);

  return (
    <AutoComplete<string, Option>
      style={{ width: 360 }}
      value={value}
      onChange={setValue}
      options={options}
      onSelect={(val) => {
        navigate(`/users/${val}`);
        setValue("");
      }}
    >
      <Input.Search placeholder="Jump to another user…" allowClear />
    </AutoComplete>
  );
};
